import React, { useState } from "react";
import { setBooking, track } from "/src/helpers/analytics";
import { useNavigate } from "react-router";
import { useApi } from "/src/helpers/Hooks/useApi";

const LoginPage = () => {
    const [bookingReference, setBookingReference] = useState("");
    const [surname, setSurname] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const api = useApi();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!bookingReference.trim() || !surname.trim()) {
            setError("Please enter your booking reference and surname.");
            return;
        }

        setIsLoading(true);
        try {
            const result = await api.login(bookingReference.trim(), surname.trim());
            if (result && result.wasSuccessful) {
                setBooking(result.reference);
                track("login", { method: "booking_reference", redirected_from_extras: !!result.redirectedFromAncillary });
                // An extras (ancillary) reference signs the customer into their main booking; the
                // homepage explains the switch once, driven by router state.
                // replace: Back from the booking page must not land on the login form again.
                navigate("/", { replace: true, state: result.redirectedFromAncillary ? { redirectedFrom: result.enteredReference } : undefined });
            } else {
                track("login_failed", { reason: result?.error ? "extras_reference" : "invalid" });
                setError(result?.error || "Login failed. Please check your login details and try again.");
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center bg-background px-4">
            <div className="w-full max-w-md bg-white rounded-card shadow-lg p-8">
                <div className="text-center mb-8">
                    <div className="text-emp mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-poppins font-semibold text-primary">
                        Welcome to My Booking
                    </h1>
                    <p className="mt-2 text-content_2 text-sm">
                        Please enter the required booking details to access your booking. You can find your Booking Reference on your email confirmation. Please use the Surname of the lead passenger as stated on the booking.    
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label
                            htmlFor="bookingReference"
                            className="block text-sm font-medium text-content_1 mb-1"
                        >
                            Booking Reference
                        </label>
                        <input
                            id="bookingReference"
                            type="text"
                            value={bookingReference}
                            onChange={(e) => setBookingReference(e.target.value)}
                            placeholder="e.g. 1001234"
                            className="w-full px-4 py-3 border border-input_border rounded-lg text-primary placeholder-content_3 focus:outline-none focus:ring-2 focus:ring-emp focus:border-transparent transition"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="surname"
                            className="block text-sm font-medium text-content_1 mb-1"
                        >
                            Lead Surname
                        </label>
                        <input
                            id="surname"
                            type="text"
                            value={surname}
                            onChange={(e) => setSurname(e.target.value)}
                            placeholder="e.g. Smith"
                            className="w-full px-4 py-3 border border-input_border rounded-lg text-primary placeholder-content_3 focus:outline-none focus:ring-2 focus:ring-emp focus:border-transparent transition"
                        />
                    </div>

                    {error && (
                        <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 bg-emp hover:bg-emp_dark text-white font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                        {isLoading ? (
                            <span className="inline-flex items-center gap-2">
                                <svg
                                    className="animate-spin h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    />
                                </svg>
                                Logging in...
                            </span>
                        ) : (
                            "Log In"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
